export type ContentLevel = 1 | 2 | 3 | 4

export type Difficulty = 'easy' | 'medium' | 'hard' | 'boss'

export type Rarity = 'common' | 'rare' | 'ultra_rare'

export type CharacterId = 'hana' | 'yuki' | 'momo' | 'sumire'

export type RoutineType = 'morning' | 'evening'

export type TaskCategory = 'work' | 'personal' | 'health' | 'learning' | 'chores' | 'creative' | 'social' | 'other'

export const TASK_CATEGORIES: { id: TaskCategory; label: string; icon: string; color: string }[] = [
  { id: 'work', label: 'Work', icon: '💼', color: 'text-blue-400 bg-blue-400/20' },
  { id: 'personal', label: 'Personal', icon: '🏠', color: 'text-purple-400 bg-purple-400/20' },
  { id: 'health', label: 'Health', icon: '💪', color: 'text-green-400 bg-green-400/20' },
  { id: 'learning', label: 'Learning', icon: '📚', color: 'text-yellow-400 bg-yellow-400/20' },
  { id: 'chores', label: 'Chores', icon: '🧹', color: 'text-orange-400 bg-orange-400/20' },
  { id: 'creative', label: 'Creative', icon: '🎨', color: 'text-pink-400 bg-pink-400/20' },
  { id: 'social', label: 'Social', icon: '👥', color: 'text-cyan-400 bg-cyan-400/20' },
  { id: 'other', label: 'Other', icon: '📌', color: 'text-gray-400 bg-gray-400/20' },
]

export interface User {
  id: string
  username: string | null
  level: number
  xp: number
  gems: number
  streakDays: number
  lastActiveDate: string | null
  contentLevel: ContentLevel
  activeCharacterId: CharacterId
  createdAt: string
}

export interface Character {
  id: CharacterId
  name: string
  description: string
  personality: string
  affection: number
  equippedOutfitId: string | null
  unlockedAt: string
}

export interface Outfit {
  id: string
  characterId: CharacterId
  name: string
  rarity: Rarity
  contentLevel: ContentLevel
  assetPath: string
  isDefault: boolean
}

export interface Task {
  id: string
  userId: string
  title: string
  description: string | null
  difficulty: Difficulty
  category: TaskCategory | null
  xpReward: number
  dueDate: string | null
  dueTime: string | null // HH:mm format
  isRecurring: boolean
  recurrencePattern: RecurrencePattern | null
  completed: boolean
  completedAt: string | null
  createdAt: string
}

export type RecurrenceType = 'daily' | 'weekly' | 'monthly' | 'yearly'

export interface RecurrencePattern {
  type: RecurrenceType
  days?: number[] // 0-6 for weekly (Sun-Sat)
  dayOfMonth?: number // 1-31 for monthly
  month?: number // 0-11 for yearly
}

export interface Habit {
  id: string
  userId: string
  title: string
  icon: string | null
  xpReward: number
  currentStreak: number
  bestStreak: number
  createdAt: string
  completedToday?: boolean
}

export interface Routine {
  id: string
  userId: string
  name: string
  type: RoutineType
  steps: RoutineStep[]
  createdAt: string
}

export interface RoutineStep {
  id: string
  title: string
  order: number
}

export interface Project {
  id: string
  userId: string
  title: string
  description: string | null
  totalHp: number
  currentHp: number
  xpReward: number
  completed: boolean
  createdAt: string
  tasks?: ProjectTask[]
}

export interface ProjectTask {
  id: string
  projectId: string
  title: string
  damage: number
  completed: boolean
  completedAt: string | null
}

export interface GachaPull {
  id: string
  userId: string
  outfitId: string
  pullType: 'single' | 'multi'
  wasPity: boolean
  createdAt: string
}

export interface PomodoroSession {
  id: string
  userId: string
  durationMinutes: number
  completed: boolean
  xpEarned: number | null
  startedAt: string
  completedAt: string | null
}

// XP calculation helpers
export const XP_PER_LEVEL_BASE = 100
export const XP_LEVEL_EXPONENT = 1.5

export function getXpForLevel(level: number): number {
  return Math.floor(XP_PER_LEVEL_BASE * Math.pow(level, XP_LEVEL_EXPONENT))
}

export function getLevelFromXp(totalXp: number): { level: number; currentXp: number; xpForNextLevel: number } {
  let level = 1
  let xpRemaining = totalXp

  while (xpRemaining >= getXpForLevel(level)) {
    xpRemaining -= getXpForLevel(level)
    level++
  }

  return {
    level,
    currentXp: xpRemaining,
    xpForNextLevel: getXpForLevel(level)
  }
}

// Difficulty XP ranges
export const DIFFICULTY_XP: Record<Difficulty, { min: number; max: number }> = {
  easy: { min: 25, max: 50 },
  medium: { min: 75, max: 150 },
  hard: { min: 200, max: 400 },
  boss: { min: 500, max: 1000 }
}

// Character data
export const CHARACTERS: Record<CharacterId, Omit<Character, 'affection' | 'equippedOutfitId' | 'unlockedAt'>> = {
  hana: {
    id: 'hana',
    name: 'Hana',
    description: 'The energetic cheerleader who always believes in you!',
    personality: 'genki'
  },
  yuki: {
    id: 'yuki',
    name: 'Yuki',
    description: 'The cool beauty who pushes you to do better.',
    personality: 'kuudere'
  },
  momo: {
    id: 'momo',
    name: 'Momo',
    description: 'The shy sweetheart who gets flustered by your success.',
    personality: 'dandere'
  },
  sumire: {
    id: 'sumire',
    name: 'Sumire',
    description: 'The nurturing mommy who\'s always proud of you~',
    personality: 'ara ara'
  }
}
